import { ActionService } from '@acme/api';
import { toCurrency, useCurrency } from '@acme/domains';
import {
  ApplicationActions,
  ApplicationActivity,
  ApplicationBaseInfo,
  ApplicationEpoch,
  ApplicationMainInfo,
  ApplicationTitle,
} from '@acme/features';
import { Page, Separator } from '@acme/foundation';
import {
  $application,
  $contract,
  $isLoading,
  applicationOpened,
  applicationReset,
} from '@entities/application';
import { useUnit } from 'effector-react';
import { Card } from 'flowbite-react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getApplicationBreadcrumbs } from '../lib/breadcrumbs';

export const ApplicationDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  useCurrency();

  const { application, contract, isLoading, onOpened, onReset } = useUnit({
    application: $application,
    contract: $contract,
    isLoading: $isLoading,
    onOpened: applicationOpened,
    onReset: applicationReset,
  });

  useEffect(() => {
    if (!id) return;
    onOpened({ id });
    return () => onReset();
  }, [id, onOpened, onReset]);

  const breadcrumbItems = useMemo(
    () => getApplicationBreadcrumbs(navigate, application?.code),
    [navigate, application?.code]
  );

  return (
    <Page breadcrumbItems={breadcrumbItems} isLoading={isLoading}>
      {!isLoading && application && (
        <>
          <Card className="divide-y divide-gray-200">
            <ApplicationTitle
              title={`${application.code} - ${application.name}`}
              currency={toCurrency(contract?.currency)}
              amount={contract?.amount}
            />
            <ApplicationBaseInfo
              application={application}
              contract={contract}
            />
            <Separator className="-mx-6" />
            <ApplicationEpoch epoch={application.epoch} />
          </Card>
          <Card className="divide-y divide-gray-200">
            <ApplicationMainInfo
              application={application}
              contract={contract}
            />

            <ApplicationActions
              applicationId={application.id ?? undefined}
              actions={application?.actions ?? null}
              confirm={{
                handler: (actionId?: string) => {
                  ActionService.postApiV1ActionConfirm({
                    id: actionId ?? '',
                    requestBody: {
                      comment: '',
                    },
                  });
                },
              }}
              reject={{
                handler: (actionId?: string) => {
                  ActionService.postApiV1ActionReject({
                    id: actionId ?? '',
                    requestBody: {
                      comment: '',
                    },
                  });
                },
              }}
              rework={{
                handler: () => {
                  navigate(`/applications/${id}/edit`);
                },
              }}
            />
          </Card>
          {application.epoch?.stepCode !== 'TRANSFER-DRAFT' && (
            <ApplicationActivity />
          )}
        </>
      )}
    </Page>
  );
};
